import React, { useMemo, useState } from 'react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import {
  Avatar, Button, Toggle, TextInput, Textarea, Select,
  BottomSheet, useToast, phone,
} from '@parsina/framework';
import MobileHeader from '@parsina/framework/clinical/src/components/mobile/MobileHeader';
import MobileSettingsNav from '../../components/mobile/MobileSettingsNav';
import {
  MobileSettingsCard, MobileFieldRow, MobileCompleteness,
} from '../../components/mobile/MobileSettingsKit';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import ConfirmDialog from '@parsina/framework/clinical/src/components/common/ConfirmDialog';
import JoinClinicPanel from '../../components/JoinClinicPanel.jsx';
import ClinicSwitchButton from '../../components/common/ClinicSwitchButton';
import SettingsCreditsPanel from './SettingsCreditsPanel';
import MobileTemplatesPanel from './MobileTemplatesPanel';
import { SettingsMobileSkeleton } from './SettingsSkeleton';
import { specialtyLabel } from '../../constants/specialties';
import { useSettingsForm } from './useSettingsForm';
import ThemePicker from './ThemePicker';
import FontFamilyPicker from './FontFamilyPicker';
import VisitLayoutEditor from './VisitLayoutEditor.mobile';
import UploadFilesSettings from './UploadFilesSettings';
import { useUploadSettings } from './useUploadSettings';
import LanguagePicker from './LanguagePicker';
import { accountUrl } from '../../lib/runtimeEnv';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { localizedThemePresets, localizedFontSizes } from './appearanceLabels';
import { useDirtyClose } from '../../components/common/DirtyCloseGuard';
import MobileAvatarSheet from '../../components/mobile/MobileAvatarSheet';
import AvatarCropModal from '../../components/common/AvatarCropModal';
import { readAvatarFile } from '@parsina/framework/clinical/src/screens/Settings/Settings.shared';
import { visibleSections } from './marketSections';
import {
  DEFAULT_SECTION,
  THEME_PRESETS, FONT_SIZES, VISIT_AI_TOGGLES, DIFFERENTIAL_SUB_TOGGLES, AI_VITAL_ALERT_TOGGLES,
  DIFFERENTIAL_POLL_INTERVALS, differentialPollSeconds, differentialPollLabel,
  vitalAlertsArmed, aiToggleImpactKey,
} from '@parsina/framework/clinical/src/screens/Settings/Settings.shared';
import SpeechEngineSetting from '@parsina/framework/clinical/src/screens/Settings/SpeechEngineSetting';
import PrescriptionSettings from '@parsina/framework/clinical/src/screens/Settings/PrescriptionSettings';

// ─────────────────────────────────────────────────────────────────────────────
// Settings — mobile (DESIGN-CONCEPT-MOBILE v2).
//
// A platform fork, not a narrowed desktop page: gradient header, a sticky
// section strip, one page-level scroll, and editing that happens in a
// BottomSheet with a docked submit rather than in a long inline form behind a
// sticky nav (§6). Behaviour is the shared `useSettingsForm` the desktop screen
// uses, so the two surfaces can look nothing alike but never disagree.
// ─────────────────────────────────────────────────────────────────────────────


// Passwords and codes are ASCII runs: they read left-to-right whatever the
// layout does. `isolate` keeps neighbouring punctuation from being dragged
// across the field.
const LTR = { direction: 'ltr', unicodeBidi: 'isolate' };

export default function SettingsMobile() {
  const s = useSettingsForm();
  const { t, has, dir, locale, formatNumber } = useTranslation();
  const themePresets = localizedThemePresets({ t, has }, THEME_PRESETS);
  const fontSizes = localizedFontSizes({ t, has }, FONT_SIZES);

  // Unlike the vital-sign thresholds — which this surface deliberately leaves
  // to the desktop — the re-assessment cadence is a pick from a short list, not
  // a number to type, so it works on a phone as well as it does on a monitor.
  const autoDiffArmed = s.ai.autoDifferentialOnStart !== false;
  const pollArmed = autoDiffArmed && s.ai.differentialPollingEnabled !== false;
  // The thresholds mean nothing while both alarms are off — same rule the
  // desktop card applies, read from the same helper.
  const alertsArmed = vitalAlertsArmed(s.ai);
  const pollOptions = useMemo(
    () => DIFFERENTIAL_POLL_INTERVALS.map((sec) => ({
      value: String(sec),
      label: differentialPollLabel(sec, t, formatNumber),
    })),
    [t, formatNumber],
  );

  // No specialty PICKER any more: the value is the council's, read against the
  // number in `medicalCode` and rendered read-only below. `specialtyLabel`
  // still translates what is stored — a catalogue value shows in the language
  // on screen, and a legacy free-text value renders verbatim.

  const toast = useToast();
  const [section, setSection] = useState(DEFAULT_SECTION);
  // «فایل‌ها» sits inside «صفحه ویزیت»; its form is read only while that section is open,
  // and leaving the section with unsaved edits asks first.
  const uploads = useUploadSettings({ enabled: section === 'visit' });
  const [pendingSection, setPendingSection] = useState(null);
  const changeSection = (id) => {
    if (id === section) return;
    if (section === 'visit' && uploads.dirty) { setPendingSection(id); return; }
    setSection(id);
  };
  const [editOpen, setEditOpen] = useState(false);
  const [joinedClinic, setJoinedClinic] = useState(null);

  const displayName = [s.form.firstName, s.form.lastName].filter(Boolean).join(' ')
    || s.doctor?.name || t('settings.profile.completeProfile');

  // F-S1 — this used to reset the form and shut the sheet with no question at
  // all, so a stray tap on the scrim (or, since overlays joined history, an
  // accidental edge-swipe) silently threw away a filled profile. `s.dirty` is
  // the same signal the SaveBar uses, so the sheet and the bar agree about
  // whether there is anything to lose.
  const closeEdit = () => { s.resetProfile(); setEditOpen(false); };
  const { requestClose: requestCloseEdit, guard: editGuard } = useDirtyClose(s.dirty, closeEdit);

  // ── The profile picture (finding 14) ──────────────────────────────────────
  // Capture was desktop-only, which is backwards: the camera is the thing the
  // PHONE has. The validation, the read and the crop are all the desktop's —
  // the picture that reaches the record must be framed the same way whichever
  // surface sent it.
  const [avatarSheet, setAvatarSheet] = React.useState(false);
  const [cropSrc, setCropSrc] = React.useState(null);
  const [confirmRemoveAvatar, setConfirmRemoveAvatar] = React.useState(false);

  const pickAvatar = async (file) => {
    const res = await readAvatarFile(file);
    if (!res.ok) { toast.error(t(res.messageKey)); return; }
    setAvatarSheet(false);
    setCropSrc(res.dataUri);
  };

  const submitProfile = async () => {
    const res = await s.saveProfile();
    if (res.ok) setEditOpen(false);
  };

  if (s.loading) {
    return (
      <Frame>
        <MobileHeader title={t('settings.title')} />
        <SettingsMobileSkeleton />
      </Frame>
    );
  }

  if (s.error && !s.doctor) {
    return (
      <Frame>
        <MobileHeader title={t('settings.title')} />
        <div data-testid="settings-error" data-state="error" style={{ padding: '1rem' }}>
          <ErrorState
            title={t('settings.profile.accountUnavailable')}
            error={s.error}
            onRetry={s.refetch}
          />
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <MobileHeader title={t('settings.title')}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          background: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: '0.75rem', padding: '0.625rem 0.75rem',
        }}>
          {/* The avatar IS the control — a separate «تغییر تصویر» button beside
              it would be a second target for one job, and the picture is the
              thing the doctor is looking at when they decide to change it. */}
          <Button
            variant="ghost"
            onClick={() => setAvatarSheet(true)}
            data-testid="settings-avatar-open"
            aria-label={s.doctor?.avatar ? t('settings.avatar.change') : t('settings.avatar.add')}
            disabled={s.avatarBusy}
            className="h-auto min-h-11 shrink-0 rounded-full p-0 hover:bg-white/15"
          >
            <Avatar name={displayName} size={44} bg="rgba(255,255,255,0.2)" src={s.doctor?.avatar || undefined} />
          </Button>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              color: 'white', fontSize: '0.9375rem', fontWeight: 700,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }} dir="auto">
              {displayName}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', marginTop: '0.125rem' }}>
              {specialtyLabel(t, s.form.specialty) || t('settings.profile.noSpecialty')}
            </div>
          </div>
        </div>
      </MobileHeader>

      {/* The sections this market has — «نسخه» is Iranian (./marketSections.js). */}
      <MobileSettingsNav sections={visibleSections()} active={section} onChange={changeSection} />
      <ConfirmDialog
        open={pendingSection !== null}
        onOpenChange={(o) => { if (!o) setPendingSection(null); }}
        title={t('settings.profile.unsavedTitle')}
        message={t('settings.profile.unsavedBody')}
        confirmLabel={t('settings.profile.unsavedLeave')}
        cancelLabel={t('settings.profile.unsavedStay')}
        destructive
        onConfirm={() => {
          const target = pendingSection;
          setPendingSection(null);
          uploads.reset();
          if (target) setSection(target);
        }}
      />

      <div
        data-testid="settings"
        data-state="content"
        style={{
          flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
          background: C.bg, padding: '0.875rem',
          display: 'flex', flexDirection: 'column', gap: '0.75rem',
        }}
      >
        {/* ── اطلاعات پزشک ── */}
        {section === 'profile' && (
          <>
            <MobileSettingsCard testId="settings-identity" title={t('settings.profile.identity')}>
              <MobileFieldRow label={t('common.labels.firstName')} value={s.form.firstName} testId="settings-field-firstName" />
              <MobileFieldRow label={t('common.labels.lastName')} value={s.form.lastName} testId="settings-field-lastName" />
              {/* Stored E.164 since the write path was migrated, so it reaches this
                  read-only row as `+98…` and must be read back as `09…`. `forDisplay`
                  is the kit's read path and never blanks — a doctor looking at a blank
                  where their number was would report it as lost data. The mobile-only
                  rule belongs on the WRITE field, not on a value already stored. */}
              <MobileFieldRow label={t('common.labels.mobile')} value={phone.forDisplay(s.form.phone)} ltr testId="settings-field-phone" />
              <MobileFieldRow label={t('common.labels.nationalId')} value={s.form.nationalId} ltr testId="settings-field-nationalId" />
              <MobileCompleteness percent={s.completeness} />
            </MobileSettingsCard>

            <MobileSettingsCard testId="settings-professional" title={t('settings.profile.professional')}>
              <MobileFieldRow label={t('common.labels.medicalCode')} value={s.form.medicalCode} ltr testId="settings-field-medicalCode" />
              <MobileFieldRow label={t('common.labels.specialty')} value={specialtyLabel(t, s.form.specialty)} testId="settings-field-specialty" />
              <MobileFieldRow label={t('common.labels.bio')} value={s.form.bio} testId="settings-field-bio" />
            </MobileSettingsCard>

            <Button
              variant="default"
              onClick={() => setEditOpen(true)}
              data-testid="settings-edit-profile"
              className="min-h-11 w-full"
            >
              {t('settings.profile.editInfo')}
            </Button>
          </>
        )}

        {/* ── دستیار هوشمند ── */}
        {section === 'ai' && (
          <>
            <MobileSettingsCard
              testId="settings-ai-visit"
              title={t('settings.ai.autoRun')}
              hint={t('settings.ai.autoRunNote')}
            >
              {VISIT_AI_TOGGLES.map((toggle) => (
                <React.Fragment key={toggle.key}>
                  <Toggle
                    id={`settings-ai-${toggle.key}`}
                    data-testid={`settings-ai-${toggle.key}`}
                    label={t(toggle.labelKey)}
                    description={t(aiToggleImpactKey(toggle, s.ai))}
                    checked={s.ai[toggle.key] !== false}
                    onChange={(v) => s.setAiSetting(toggle.key, v)}
                  />

                  {/* Indented under the switch it qualifies, and dimmed when
                      that switch is off — same relationship the desktop draws
                      with a rule, expressed with the space a phone has. */}
                  {toggle.key === 'autoDifferentialOnStart' && (
                    <div
                      data-testid="settings-ai-differential-polling"
                      style={{
                        marginTop: 10, paddingInlineStart: 12,
                        borderInlineStart: `2px solid ${C.border}`,
                        opacity: autoDiffArmed ? 1 : 0.55,
                      }}
                    >
                      {DIFFERENTIAL_SUB_TOGGLES.map((sub) => (
                        <Toggle
                          key={sub.key}
                          id={`settings-ai-${sub.key}`}
                          data-testid={`settings-ai-${sub.key}`}
                          label={t(sub.labelKey)}
                          description={t(sub.impactKey)}
                          checked={s.ai[sub.key] !== false}
                          disabled={!autoDiffArmed}
                          onChange={(v) => s.setAiSetting(sub.key, v)}
                        />
                      ))}
                      <div style={{ marginTop: 10 }}>
                        <Select
                          label={t('settings.ai.pollInterval')}
                          data-testid="settings-ai-pollInterval"
                          value={String(differentialPollSeconds(s.ai))}
                          options={pollOptions}
                          placeholder=""
                          disabled={!pollArmed}
                          helperText={t('settings.ai.pollIntervalHint')}
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            if (Number.isFinite(n) && n > 0) {
                              s.setAiSetting('differentialPollIntervalSec', n);
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </MobileSettingsCard>

            {/* How the doctor's voice becomes text — «ثبت صوتی» and the
                visit recording. The same control as the desktop card. */}
            <MobileSettingsCard
              testId="settings-ai-speech"
              title={t('settings.ai.speech.title')}
              hint={t('settings.ai.speech.hint')}
            >
              <SpeechEngineSetting ai={s.ai} onChange={(v) => s.setAiSetting('speechEngine', v)} />
            </MobileSettingsCard>

            {/* Vital-sign alarms only. The test/imaging/drug suggestion
                switches are gone with the suggestions themselves — those are
                ordered in «دستورات» and «نسخه» now. */}
            <MobileSettingsCard
              testId="settings-ai-alerts"
              title={t('settings.ai.vitalAlerts')}
              hint={t('settings.ai.thresholdHint')}
            >
              {AI_VITAL_ALERT_TOGGLES.map((toggle) => (
                <Toggle
                  key={toggle.key}
                  id={`settings-ai-${toggle.key}`}
                  data-testid={`settings-ai-${toggle.key}`}
                  label={t(toggle.labelKey)}
                  checked={s.ai[toggle.key] !== false}
                  onChange={(v) => s.setAiSetting(toggle.key, v)}
                />
              ))}

              {/* The numbers themselves. This card's hint has always said
                  «آستانه‌ها تعیین می‌کنند از چه عددی هشدار داده شود» while
                  rendering no thresholds at all: a doctor on a phone could arm
                  the alarms and not choose what arms them, and had to find a
                  desktop to change a number they were being interrupted by.
                  Stacked rather than side by side — two number fields in a row
                  on a phone are two fields nobody can read the labels of. */}
              <div
                data-testid="settings-ai-thresholds"
                style={{
                  marginTop: 12, paddingInlineStart: 12,
                  borderInlineStart: `2px solid ${C.border}`,
                  display: 'flex', flexDirection: 'column', gap: 12,
                  opacity: alertsArmed ? 1 : 0.55,
                }}
              >
                <div data-testid="settings-ai-spo2">
                  <TextInput
                    label={t('settings.ai.spo2Threshold')}
                    {...s.thresholdField('spo2Threshold')}
                    inputMode="numeric"
                    dir="ltr"
                    disabled={!alertsArmed}
                  />
                </div>
                <div data-testid="settings-ai-bp">
                  <TextInput
                    label={t('settings.ai.bpThreshold')}
                    {...s.thresholdField('bpSystolicThreshold')}
                    inputMode="numeric"
                    dir="ltr"
                    disabled={!alertsArmed}
                  />
                </div>
              </div>
            </MobileSettingsCard>
          </>
        )}

        {/* ── صفحه ویزیت ── */}
        {section === 'visit' && (
          <MobileSettingsCard
            testId="settings-visit-layout"
            title={t('settings.visit.title')}
            hint={t('settings.visit.subtitle')}
          >
            <VisitLayoutEditor
              settings={s.visitLayout}
              onChange={s.setVisitLayout}
              onReset={s.resetVisitLayout}
            />
          </MobileSettingsCard>
        )}
        {section === 'visit' && (
          <MobileSettingsCard
            testId="settings-files-card"
            title={t('settings.files.title')}
            hint={t('settings.files.subtitle')}
          >
            <UploadFilesSettings s={uploads} compact />
          </MobileSettingsCard>
        )}

        {/* ── قالب‌ها ── */}
        {section === 'templates' && <MobileTemplatesPanel />}

        {/* ── نسخه ── the visit code's inputs; same panel as desktop, one column. */}
        {section === 'prescription' && (
          <MobileSettingsCard
            testId="settings-prescription-card"
            title={t('settings.prescription.title')}
            hint={t('settings.prescription.subtitle')}
          >
            <PrescriptionSettings compact />
          </MobileSettingsCard>
        )}

        {/* ── اعتبار ── */}
        {section === 'credits' && (
          <SettingsCreditsPanel isMobile />
        )}

        {/* ── زبان ──
            Its own section, not a row under «ظاهر برنامه»: the one setting that
            has to be findable by someone who cannot read the screen it is on.
            The «EN»/«FA» tag chips in the picker are what make that work. */}
        {section === 'language' && (
          <MobileSettingsCard
            testId="settings-language"
            title={t('settings.language.title')}
            hint={t('settings.language.subtitle')}
          >
            <LanguagePicker value={s.language} onChange={s.setLanguage} />
            <div
              data-testid="settings-language-direction"
              style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: C.textMuted, lineHeight: 1.8 }}
            >
              {t('settings.language.direction.title')}:{' '}
              {dir === 'rtl'
                ? t('settings.language.direction.rtl')
                : t('settings.language.direction.ltr')}
              {' — '}
              {t('settings.language.direction.note')}
            </div>
          </MobileSettingsCard>
        )}

        {/* ── ظاهر برنامه ── */}
        {section === 'appearance' && (
          <>
            <MobileSettingsCard
              testId="settings-appearance"
              title={t('settings.appearance.themeTitle')}
              hint={t('settings.appearance.themeSubtitle')}
            >
              <ThemePicker
                presets={themePresets}
                value={s.appearance.theme}
                onChange={(id) => s.setAppearanceSetting('theme', id)}
              />
            </MobileSettingsCard>

            <MobileSettingsCard testId="settings-fontsize" title={t('settings.appearance.fontSizeTitle')}>
              <div
                role="radiogroup"
                aria-label={t('settings.appearance.fontSizeTitle')}
                data-testid="settings-fontsize-group"
                style={{ display: 'flex', gap: '0.5rem' }}
              >
                {fontSizes.map((f) => {
                  const on = s.appearance.fontSize === f.id;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      tabIndex={on ? 0 : -1}
                      data-testid={`settings-fontsize-${f.id}`}
                      onClick={() => s.setAppearanceSetting('fontSize', f.id)}
                      style={{
                        flex: 1, minHeight: '2.75rem', cursor: 'pointer',
                        borderRadius: '0.625rem',
                        background: on ? C.card : 'transparent',
                        border: `1.5px solid ${on ? C.navy : C.border}`,
                        color: on ? C.textPrimary : C.textSecondary,
                        fontWeight: on ? 700 : 600,
                        fontSize: f.id === 'small' ? '0.8125rem' : f.id === 'large' ? '1.0625rem' : '0.9375rem',
                      }}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </MobileSettingsCard>

            <MobileSettingsCard
              testId="settings-fontfamily"
              title={t('settings.appearance.fontFamilyTitle')}
              hint={t('settings.appearance.fontFamilySubtitle')}
            >
              <FontFamilyPicker
                value={s.appearance.fontFamily}
                onChange={(id) => s.setAppearanceSetting('fontFamily', id)}
              />
            </MobileSettingsCard>
          </>
        )}

        {/* ── مطب‌ها ── */}
        {section === 'clinics' && (
          <MobileSettingsCard
            testId="settings-clinics"
            title={t('settings.clinics.join')}
            hint={t('settings.clinics.joinHint')}
          >
            {joinedClinic ? (
              <div
                role="status"
                style={{
                  background: C.greenLight, borderRadius: '0.625rem',
                  padding: '0.75rem', color: C.green, fontSize: '0.8125rem', lineHeight: 1.8,
                }}
                dir="auto"
              >
                {t('clinic.joinRequested', { name: joinedClinic.clinic?.name || t('common.labels.clinic') })}
                {joinedClinic.status === 'pending' && t('settings.clinics.pendingSuffix')}
              </div>
            ) : (
              <JoinClinicPanel showTitle={false} onJoined={setJoinedClinic} />
            )}
            {/* Only renders for an account that actually has a clinic panel to
                enter — see ClinicSwitchButton. */}
            <ClinicSwitchButton className="w-full mt-3" />
          </MobileSettingsCard>
        )}

        {/* ── امنیت ── */}
        {section === 'security' && (
          <>
            <MobileSettingsCard testId="settings-signin" title={t('settings.security.login')}>
              <Toggle
                id="settings-security-otpEnabled"
                data-testid="settings-security-otpEnabled"
                label={t('settings.security.otp')}
                description={t('settings.security.otpHint')}
                checked={s.security.otpEnabled === true}
                onChange={(v) => s.setSecuritySetting('otpEnabled', v)}
              />
              <Toggle
                id="settings-security-biometricEnabled"
                data-testid="settings-security-biometricEnabled"
                label={t('settings.security.biometric')}
                description={t('settings.security.biometricHint')}
                checked={s.security.biometricEnabled === true}
                onChange={(v) => s.setSecuritySetting('biometricEnabled', v)}
              />
            </MobileSettingsCard>

            {/* Password lives in حساب پارسینا now. It used to exist only in this
                panel, so every other role had no way to change theirs — and an
                account that signed up by phone and never chose one could not set
                a first password here at all, because the form demanded the
                current one. */}
            <a
              href={accountUrl('/security')}
              data-testid="settings-open-password"
              className="min-h-11 w-full"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 10, background: C.navy, color: '#fff',
                fontSize: '0.875rem', fontWeight: 700, textDecoration: 'none' }}
            >
              {t('settings.account.accountLinkSecurity')}
            </a>
          </>
        )}
      </div>

      {/* ── Profile edit sheet — full-width inputs, one per row, docked submit ── */}
      <MobileAvatarSheet
        open={avatarSheet}
        onClose={() => setAvatarSheet(false)}
        onPick={pickAvatar}
        onRemove={() => { setAvatarSheet(false); setConfirmRemoveAvatar(true); }}
        hasAvatar={Boolean(s.doctor?.avatar)}
      />

      {/* The SAME crop the desktop uses — a phone-only framing would be a
          second opinion about what the picture in the record looks like. */}
      <AvatarCropModal
        open={Boolean(cropSrc)}
        onOpenChange={(open) => { if (!open) setCropSrc(null); }}
        src={cropSrc}
        busy={s.avatarBusy}
        onCrop={async (dataUri) => {
          const res = await s.saveAvatar(dataUri);
          if (res.ok) setCropSrc(null);
        }}
      />

      <ConfirmDialog
        open={confirmRemoveAvatar}
        onOpenChange={setConfirmRemoveAvatar}
        title={t('settings.avatar.removeTitle')}
        message={t('settings.avatar.removeBody')}
        confirmLabel={t('settings.avatar.remove')}
        destructive
        onConfirm={() => s.removeAvatar()}
      />

      <BottomSheet open={editOpen} onClose={requestCloseEdit} title={t('settings.profile.editInfo')} closeLabel={t('common.actions.close')}>
        {editGuard}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Name, mobile and کد ملی are edited in حساب پارسینا. What is left
              here is professional: نظام پزشکی، تخصص، بیو — what you are licensed
              to do, not who you are. */}
          <a href={accountUrl('/identity')} data-testid="settings-edit-identity-link"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
              minHeight: 44, borderRadius: 10, border: `1.5px solid ${C.border}`,
              color: C.ink2, fontSize: '0.84375rem', fontWeight: 600, textDecoration: 'none' }}>
            {t('settings.account.accountLinkIdentity')}
          </a>
          {/* Locked once stated — the number is this account's identity in
              سازمان نظام پزشکی, not a profile field. See
              Settings.web.jsx; the BFF drops it on save either way. */}
          {s.councilLocked ? (
            <TextInput
              label={t('common.labels.medicalCode')}
              data-testid="settings-medicalCode"
              value={s.form.medicalCode}
              onChange={() => {}}
              disabled
              inputStyle={LTR}
              helperText={t('settings.profile.medicalCodeLocked')}
            />
          ) : (
            <TextInput label={t('common.labels.medicalCode')} data-testid="settings-medicalCode" value={s.form.medicalCode} onChange={s.setField('medicalCode')} required error={s.errors.medicalCode && t(s.errors.medicalCode)} inputStyle={LTR} />
          )}
          {/* Read-only: the specialty belongs to the council record behind
              the number above, not to this form. See Settings.web.jsx. */}
          <TextInput
            label={t('common.labels.specialty')}
            data-testid="settings-specialty"
            value={specialtyLabel(t, s.form.specialty)}
            onChange={() => {}}
            disabled
          />
          <div style={{ fontSize: '0.7rem', color: C.textSecondary, lineHeight: 1.8 }}>
            {t('settings.profile.fromCouncil')}
          </div>
          <Textarea label={t('common.labels.bio')} data-testid="settings-bio" value={s.form.bio} onChange={s.setField('bio')} rows={3} />
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <Button variant="ghost" onClick={requestCloseEdit} className="min-h-11 flex-1" data-testid="settings-edit-cancel">
            {t('common.actions.cancel')}
          </Button>
          <Button variant="default" onClick={submitProfile} disabled={s.saving} className="min-h-11 flex-1" data-testid="settings-edit-submit">
            {s.saving ? t('common.state.saving') : t('common.actions.save')}
          </Button>
        </div>
      </BottomSheet>

    </Frame>
  );
}

// The mobile shell is height-constrained, so the screen owns its own scroll
// region and the header/nav stay put above it.
function Frame({ children }) {
  return (
    // No `direction` here — it is inherited from <html dir>, which I18nProvider
    // owns. Pinning it would keep the mobile shell right-to-left in English.
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: C.bg, overflow: 'hidden',
    }}>
      {children}
    </div>
  );
}
